<template>
	<div
		:class="{
			'create-edit-job': true,
			'create-edit-job--is-dragging-over': isDraggingOver,
			'create-edit-job--can-drop': canDrop && isDraggingOver,
			'create-edit-job--cannot-drop': !canDrop && isDraggingOver,
		}"
		@dragover="handleDragOver"
		@dragenter="handleDragEnter"
		@dragleave="handleDragLeave"
		@drop="handleDrop"
	>
		<app-loader
			v-if="isLoading"
			:style="loaderStyle"
		/>
		<div
			:class="{
				'create-edit-job__drop-zone': true,
				'create-edit-job__drop-zone--can-drop': canDrop && isDraggingOver,
				'create-edit-job__drop-zone--cannot-drop': !canDrop && isDraggingOver,
			}"
		/>
		<file-info-modal
			ref="$fileInfoModal"
			:title="$t('file_information_and_access')"
		/>
		<div class="create-edit-job__header">
			<div class="create-edit-job__header-title">
				{{ $t(props.type === 'create' ? 'job_create_title' : 'job_edit_title') }}
			</div>
			<app-tooltip :text="$t('close_label')">
				<template #activator>
					<app-icon
						class="create-edit-job__header-close"
						name="close"
						width="20px"
						color="#979797"
						hover-color="#499259"
						cursor="pointer"
						@click="emit('on-close')"
					/>
				</template>
			</app-tooltip>
		</div>
		<div class="create-edit-job__body">
			<app-select
				v-model="filters.WorkerIds"
				:placeholder="$t('job_workers_placeholder')"
				:items="props.workers"
				:pinned-items="props.pinnedWorkers ?? []"
				:disabled="props.type === 'edit'"
				selected-count-key="job_workers_count"
				description-key="status"
				searchable
				multiple
			></app-select>
			<div class="create-edit-job__body-options">
				<div class="create-edit-job__body-options-checkbox">
					<app-checkbox
						:value="filters.NeedChecking"
						@on-input="toggleNeedChecking"
					/>
					<div class="create-edit-job__body-options-checkbox-label">
						{{ $t('job_with_report') }}
					</div>
					<app-date-string
						v-model="filters.ToDoDate"
						:min-date="new Date()"
					></app-date-string>
					<app-tooltip
						v-if="filters.ToDoDate"
						:text="$t('date_reset')"
					>
						<template #activator>
							<app-icon
								class="create-edit-job__body-options-date-clear"
								name="close-bold"
								width="12px"
								color="#D2D2D2"
								hover-color="#BA4C4C"
								cursor="pointer"
								@click="clearToDoDate"
							/>
						</template>
					</app-tooltip>
				</div>
				<app-icon
					name="file-text"
					width="18px"
					:color="showTemplates ? '#3F884F' : '#979797'"
					hover-color="#499259"
					cursor="pointer"
					@click="toggleTemplates"
				/>
			</div>
			<app-textarea
				class="create-edit-job__textarea"
				v-model="filters.Message"
				height="100%"
				:placeholder="$t('job_message_placeholder')"
			/>
			<app-select
				ref="$templatesSelect"
				class="create-edit-job__templates"
				v-if="showTemplates"
				v-model="selectedTemplate"
				:placeholder="$t('job_template_placeholder')"
				:items="mappedTemplates"
				searchable
				clearable
				@search="searchedTemplate = $event"
				@close="closeTemplateConfirm"
			>
				<template #append-icon>
					<app-tooltip
						v-if="searchedTemplate"
						:text="$t('job_template_add')"
					>
						<template #activator>
							<app-icon
								name="plus"
								width="16px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								@click="createTemplate"
							/>
						</template>
					</app-tooltip>
				</template>
				<template #option-append="{ item, hovered }">
					<app-tooltip
						v-if="hovered || templateToDelete?.id === item.id"
						:text="$t('job_template_remove')"
					>
						<template #activator>
							<app-icon
								name="delete-outline"
								width="16px"
								color="#979797"
								hover-color="#BA4C4C"
								cursor="pointer"
								@click.stop="openTemplateDeleteConfirm(item, $event)"
							/>
						</template>
					</app-tooltip>
					<div
						v-if="templateToDelete?.id === item.id"
						class="create-edit-job__template-confirm"
						:style="templateConfirmStyle"
						@click.stop
						@mousedown.stop
						@keydown.stop
					>
						<confirm-action-dropdown
							:title="$t('job_template_delete_title')"
							:description="$t('job_template_delete_question', { name: templateToDelete.label })"
							:confirm-label="$t('remove')"
							@cancel="closeTemplateConfirm"
							@confirm="confirmTemplateDelete"
						/>
					</div>
				</template>
			</app-select>
			<div
				:class="{
					'create-edit-job__body-attachments': true,
					'create-edit-job__body-attachments--have-files': hasAnyFiles,
				}"
			>
				<div class="create-edit-job__body-files-block">
					<input
						ref="$uploadFile"
						type="file"
						class="create-edit-job__upload-input"
						multiple
						@change="handleFileInput"
					/>
					<app-tooltip :text="$t('file_upload')">
						<template #activator>
							<app-icon
								name="clip"
								width="18px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
								@click="triggerFileInput"
							/>
						</template>
					</app-tooltip>
					<div
						v-if="hasAnyFiles"
						class="create-edit-job__body-files"
					>
						<div
							v-for="file in existingFiles"
							:key="`exist-${file.Id}`"
							class="create-edit-job__body-files-item"
						>
							<app-tooltip :text="$t('information')">
								<template #activator>
									<app-icon
										class="create-edit-job__body-files-item-icon"
										name="info-circle-outline"
										width="18px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
										@click="showFileInfo(file)"
									/>
								</template>
							</app-tooltip>
							<app-tooltip
								class="create-edit-job__body-files-item-name-tooltip"
								:text="$t('file_download')"
							>
								<template #activator>
									<div
										class="create-edit-job__body-files-item-name"
										@click="downloadFile(file.Id)"
									>
										{{ file.Name }}
									</div>
								</template>
							</app-tooltip>
							<app-tooltip :text="$t('file_detach')">
								<template #activator>
									<app-icon
										class="create-edit-job__body-files-item-delete"
										name="close-bold"
										width="12px"
										color="#D2D2D2"
										hover-color="#BA4C4C"
										cursor="pointer"
										@click="detachExistingFile(file)"
									/>
								</template>
							</app-tooltip>
							<app-tooltip
								v-if="fileLocationTarget"
								:text="$t('file_go_to_location')"
							>
								<template #activator>
									<app-icon
										class="create-edit-job__body-files-item-icon"
										name="arrow-45deg"
										width="18px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
										@click="goToFileLocation"
									/>
								</template>
							</app-tooltip>
						</div>
						<div
							v-for="(file, index) in pendingProjectFiles"
							:key="`pp-${index}-${file.Id}`"
							class="create-edit-job__body-files-item"
						>
							<div class="create-edit-job__body-files-item-name">
								{{ file.Name }}
							</div>
							<app-tooltip :text="$t('file_remove_from_list')">
								<template #activator>
									<app-icon
										class="create-edit-job__body-files-item-delete"
										name="close-bold"
										width="12px"
										color="#D2D2D2"
										hover-color="#BA4C4C"
										cursor="pointer"
										@click="removePendingProjectFile(index)"
									/>
								</template>
							</app-tooltip>
						</div>
						<div
							v-for="(file, index) in pendingFiles"
							:key="`pf-${index}-${file.name}`"
							class="create-edit-job__body-files-item"
						>
							<div class="create-edit-job__body-files-item-name">
								{{ file.name }}
							</div>
							<app-tooltip :text="$t('file_remove_from_list')">
								<template #activator>
									<app-icon
										class="create-edit-job__body-files-item-delete"
										name="close-bold"
										width="12px"
										color="#D2D2D2"
										hover-color="#BA4C4C"
										cursor="pointer"
										@click="removePendingFile(index)"
									/>
								</template>
							</app-tooltip>
						</div>
					</div>
				</div>
				<div class="create-edit-job__body-tags-block">
					<app-resizable-dropdown
						ref="$tagsDropdown"
						position="right"
						:close-by-click-outside="true"
						:max-width="640"
						:max-height="460"
					>
						<template #activator>
							<app-tooltip :text="$t('tags_upcase')">
								<template #activator>
									<app-icon
										class="create-edit-job__body-tags-add"
										name="hash"
										width="18px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
									/>
								</template>
							</app-tooltip>
						</template>
						<app-tag-selector
							:model-value="filters.Tags"
							:entity-type="TagEntityType.Job"
							mode="select"
							allow-create
							@update:model-value="onTagSelectorChange"
							@create-tag="onCreateTag"
							@close="$tagsDropdown?.close()"
						/>
					</app-resizable-dropdown>
					<span
						v-for="tag in filters.Tags"
						class="create-edit-job__body-tag"
						:key="tag.Id || tag.Name"
					>
						{{ tag.Name.toLowerCase().trim() }}
						<app-tooltip :text="$t('ui_tag_detach')">
							<template #activator>
								<app-icon
									class="create-edit-job__body-tag-delete"
									name="close-bold"
									width="12px"
									color="#D2D2D2"
									hover-color="#BA4C4C"
									cursor="pointer"
									@click="removeTag(tag)"
								/>
							</template>
						</app-tooltip>
					</span>
				</div>
			</div>
		</div>
		<div class="create-edit-job__divider" />
		<div class="create-edit-job__footer">
			<app-button
				:label="$t('save')"
				@click="onSave"
				:disabled="isLoading || !isFormValid"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	onUnmounted,
	ref,
	watch
} from "vue";
import AppTextarea from "@/components/ui-kit/textarea/AppTextarea.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppSelect from "@/components/ui-kit/select/AppSelect.vue";
import type { SelectItem } from "@/components/ui-kit/select/AppSelect.vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppButton from "@/components/ui-kit/button/AppButton.vue";
import AppDateString from "@/components/ui-kit/datepicker/AppDateString.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import ConfirmActionDropdown from "@/components/main/clients-panel/dropdowns/ConfirmActionDropdown.vue";
import FileInfoModal from "@/components/file/modals/FileInfoModal.vue";
import {
	Job,
	JobFile,
	NewJob,
	jobsStore
} from "@/store/jobs";
import {JobsApi, Template} from "@/ api/JobsApi";
import {FileApi} from "@/ api/FileApi";
import {workerStore} from "@/store/worker";
import { useFileDrop, MAX_FILES_PER_BATCH } from "@/composables/useFileDrop";
import { useNotification } from "@/composables/useNotification";
import { useUploadQueue } from "@/composables/useUploadQueue";
import { trimTrailingLineBreaks } from "@/utils/textHelpers";
import { i18n, tp } from "@/i18n";
import { TagBaseType, TagEntityType } from "@/models/etc/types";
import type { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { useRoute, useRouter } from "vue-router";

type CreateEditContactProps = {
	job?: Job,
	workers: any[]
	// Строки, закреплённые над списком исполнителей (текущий пользователь)
	pinnedWorkers?: any[]
	type?: 'create' | 'edit'
}

type JobFormState = {
	WorkerIds: number[] | null
	NeedChecking: boolean
	ToDoDate: Date | null
	Message: string
	Tags: TagWithFrequency[]
}

type PendingProjectFile = {
	Id: number
	Name: string
	Signed: boolean
}

const MAX_SIZE_MB = 100

const emit = defineEmits<{
	(e: 'on-close'): void
}>()

const props = withDefaults(defineProps<CreateEditContactProps>(), {
	type: 'create'
})

const t = (key: string, named?: Record<string, unknown>): string => {
	return String(named ? i18n.global.t(key, named) : i18n.global.t(key))
}

const store = jobsStore()
const userStore = workerStore()
const route = useRoute()
const router = useRouter()
const notify = useNotification()
const uploadQueue = useUploadQueue()

const isLoading = ref(false)
const templates = ref<Template[]>([])
const showTemplates = ref(false)
const selectedTemplate = ref<number | null>(null)
const searchedTemplate = ref<string | null>(null)
const templateToDelete = ref<{ id: number, label: string } | null>(null)
const templateConfirmStyle = ref({ top: '0px', left: '0px' })
const isTemplateDeleting = ref(false)

const filters = ref<JobFormState>({
	WorkerIds: null,
	NeedChecking: false,
	ToDoDate: null,
	Message: '',
	Tags: []
})

const $uploadFile = ref<HTMLInputElement | null>(null)
const $tagsDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $templatesSelect = ref<InstanceType<typeof AppSelect> | null>(null)
const $fileInfoModal = ref<typeof FileInfoModal | null>(null)

const pendingFiles = ref<File[]>([])
const pendingProjectFiles = ref<PendingProjectFile[]>([])

const {
	isDraggingOver,
	canDrop,
	validateFile,
	handleDragOver,
	handleDragEnter,
	handleDragLeave,
	handleDrop,
} = useFileDrop({
	maxSizeMb: MAX_SIZE_MB,
	maxFiles: MAX_FILES_PER_BATCH,
	multiple: true,
	onError: (message) => notify.danger(t('files_label'), message),
	onFiles: async (files) => {
		if (props.type === 'create') {
			for (const file of files) pendingFiles.value.push(file)
			return
		}
		if (props.job) enqueueDeviceUpload(files, props.job.Id)
	},
	onProjectFiles: async (files) => {
		if (props.type === 'create') {
			for (const f of files) {
				pendingProjectFiles.value.push({ Id: f.Id, Name: f.Name, Signed: f.Signed })
			}
			return
		}
		if (props.job) await attachProjectFilesToJob(files, props.job.Id)
	},
})

const currentUser = computed(() => userStore.workerProfile)

const mappedTemplates = computed(() => templates.value.map(template => ({
	id: template.Id,
	label: template.Name,
	type: 'item'
})))

const existingFiles = computed<JobFile[]>(() => {
	if (props.type !== 'edit' || !props.job?.Files) {
		return []
	}

	return props.job.Files
})

const hasAnyFiles = computed(() =>
	existingFiles.value.length > 0
	|| pendingFiles.value.length > 0
	|| pendingProjectFiles.value.length > 0
)

const fileLocationTarget = computed(() => {
	if (props.type !== 'edit' || !props.job) {
		return null
	}
	if (props.job.Project) {
		return { page: 'project' as const, id: props.job.Project.Id }
	}
	const firstClientId = props.job.Clients?.[0]?.Id
	if (firstClientId && firstClientId !== 1) {
		return { page: 'client' as const, id: firstClientId }
	}

	return null
})

const enqueueDeviceUpload = (files: File[], jobId: number) => {
	uploadQueue.enqueue(files, (file, hooks) =>
		store.attachJobFileFromDevice(file, jobId, hooks)
	)
}

const notifyAttachResult = (ok: number, fail: number) => {
	if (ok > 0 && fail === 0) {
		notify.success(t('files_label'), tp('file_attached_count', ok))
	} else if (ok > 0 && fail > 0) {
		notify.warning(t('files_label'), t('file_attach_partial', { ok, fail }))
	} else if (fail > 0) {
		notify.danger(t('files_label'), tp('file_attach_failed_count', fail))
	}
}

const attachProjectFilesToJob = async (metas: PendingProjectFile[], jobId: number) => {
	let ok = 0
	let fail = 0
	for (const m of metas) {
		try {
			await store.attachJobFileFromClientOrProject(jobId, m.Id, {
				Id: m.Id,
				Name: m.Name,
				Signed: m.Signed,
			})
			ok++
		} catch (e) {
			console.error(e)
			fail++
		}
	}
	notifyAttachResult(ok, fail)
}

const triggerFileInput = () => {
	$uploadFile.value?.click()
}

const handleFileInput = async (event: Event) => {
	const target = event.target as HTMLInputElement
	if (!target.files || target.files.length === 0) {
		return
	}

	const accepted: File[] = []
	for (const file of Array.from(target.files)) {
		const validation = validateFile(file)
		if (!validation.isValid) {
			notify.danger(t('files_label'), validation.error!)
			continue
		}
		accepted.push(file)
	}
	target.value = ''
	if (accepted.length === 0) return

	let files = accepted
	if (files.length > MAX_FILES_PER_BATCH) {
		files = files.slice(0, MAX_FILES_PER_BATCH)
		notify.warning(t('files_label'), t('file_batch_limit', { count: MAX_FILES_PER_BATCH }))
	}
	if (props.type === 'create') {
		for (const file of files) {
			pendingFiles.value.push(file)
		}
		return
	}
	if (props.job) {
		enqueueDeviceUpload(files, props.job.Id)
	}
}

const removePendingFile = (index: number) => {
	pendingFiles.value.splice(index, 1)
}

const removePendingProjectFile = (index: number) => {
	pendingProjectFiles.value.splice(index, 1)
}

const detachExistingFile = async (file: JobFile) => {
	if (!props.job) return

	try {
		isLoading.value = true
		await store.detachJobFile(props.job.Id, file.Id)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const showFileInfo = async (file: JobFile) => {
	try {
		isLoading.value = true
		const containerFile = await FileApi.GetFileInfo(file.Id)
		if (containerFile) {
			$fileInfoModal.value?.onOpen(containerFile, "file")
		}
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const downloadFile = (fileId: number) => {
	try {
		FileApi.FileDownload(fileId)
	} catch (e) {
		console.error(e)
	}
}

const goToFileLocation = () => {
	if (!fileLocationTarget.value) return

	router.push({
		query: {
			...route.query,
			page: fileLocationTarget.value.page,
			id: fileLocationTarget.value.id
		}
	})
}

const onTagSelectorChange = (nextTags: TagWithFrequency[]) => {
	filters.value.Tags = nextTags
}

// Работа ещё может не существовать (создание), а в редактировании теги уходят
// одним пакетом по «Сохранить», поэтому новый тег кладём только в локальный стейт:
// на бэке он создастся по имени
const onCreateTag = (name: string) => {
	const normalized = name.trim().toLowerCase()
	if (!normalized) {
		return
	}
	const current = filters.value.Tags ?? []
	if (current.some(tag => tag.Name.toLowerCase().trim() === normalized)) {
		return
	}
	const tempId = Math.min(0, ...current.map(tag => tag.Id)) - 1
	filters.value.Tags = [...current, {
		Id: tempId,
		Name: normalized,
		Type: TagBaseType.Common,
		Frequency: 0,
		FullName: normalized,
	}]
}

const removeTag = (tag: TagWithFrequency) => {
	filters.value.Tags = filters.value.Tags.filter(t =>
		t.Id !== tag.Id || t.Name.toLowerCase() !== tag.Name.toLowerCase()
	)
}

const buildCreatePayload = (): NewJob => {
	return {
		WorkerIds: filters.value.WorkerIds,
		NeedChecking: filters.value.NeedChecking,
		ToDoDate: filters.value.ToDoDate ? filters.value.ToDoDate.toISOString() : null,
		Message: trimTrailingLineBreaks(filters.value.Message),
		Tags: filters.value.Tags.map(t => t.Name),
	}
}

const onSave = async () => {
	try {
		isLoading.value = true
		if (props.type === 'create') {
			const created = await store.createJob(
				buildCreatePayload(),
				currentUser.value?.Id as number
			)
			if (created && created.length > 0) {
				for (const job of created) {
					if (pendingFiles.value.length > 0) {
						enqueueDeviceUpload(pendingFiles.value.slice(), job.Id)
					}
					if (pendingProjectFiles.value.length > 0) {
						await attachProjectFilesToJob(pendingProjectFiles.value.slice(), job.Id)
					}
				}
			}
			localStorage.removeItem('newJob')
		} else if (props.job) {
			await store.editJob({
				JobId: props.job.Id,
				WorkerId: filters.value.WorkerIds?.[0],
				NeedChecking: filters.value.NeedChecking,
				ToDoDate: filters.value.ToDoDate ? filters.value.ToDoDate.toISOString() : null,
				Message: trimTrailingLineBreaks(filters.value.Message),
				Tags: filters.value.Tags.map(t => t.Name),
			})
		}
		emit('on-close')
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onOpen = async () => {
	try {
		isLoading.value = true
		if (props.type === 'create') {
			const newJobFromLS = localStorage.getItem('newJob')
			if (newJobFromLS) {
				const parsed = JSON.parse(newJobFromLS) as Partial<JobFormState>
				filters.value = {
					WorkerIds: parsed.WorkerIds ?? null,
					NeedChecking: parsed.NeedChecking ?? false,
					ToDoDate: parsed.ToDoDate
						? (typeof parsed.ToDoDate === 'string' ? new Date(parsed.ToDoDate) : parsed.ToDoDate)
						: null,
					Message: parsed.Message ?? '',
					Tags: (parsed.Tags ?? []) as TagWithFrequency[],
				}
			} else {
				filters.value = {
					WorkerIds: null,
					NeedChecking: false,
					ToDoDate: null,
					Message: '',
					Tags: []
				}
			}
		} else if (props.job) {
			filters.value = {
				WorkerIds: props.job.Worker ? [props.job.Worker.Id] : [],
				NeedChecking: props.job.NeedChecking,
				ToDoDate: props.job.ToDoDate ? new Date(props.job.ToDoDate) : null,
				Message: props.job.Message,
				Tags: (props.job.Tags ?? []) as unknown as TagWithFrequency[],
			}
		}

		templates.value = await JobsApi.getJobsTemplates()
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const toggleTemplates = () => {
	showTemplates.value = !showTemplates.value

	if (!showTemplates.value) {
		return
	}

	nextTick(() => $templatesSelect.value?.open())
}

const getTomorrow = (): Date => {
	const date = new Date()
	date.setDate(date.getDate() + 1)
	date.setHours(0, 0, 0, 0)

	return date
}

// Дата сама по себе необязательна, но с включённым «С отчетом» пустой остаться не может
const toggleNeedChecking = (value: boolean) => {
	filters.value.NeedChecking = value

	if (value && !filters.value.ToDoDate) {
		filters.value.ToDoDate = getTomorrow()
	}
}

const clearToDoDate = () => {
	filters.value.ToDoDate = filters.value.NeedChecking ? getTomorrow() : null
}

const createTemplate = async () => {
	const isSearchedTemplateExist = templates.value.find(template => template.Name === searchedTemplate.value)

	if (!isSearchedTemplateExist && searchedTemplate.value) {
		try {
			isLoading.value = true
			const result = await JobsApi.createJobsTemplate({
				Name: searchedTemplate.value,
				Text: filters.value.Message
			})

			templates.value.unshift({
				Id: result.Id,
				Name: searchedTemplate.value,
				Text: filters.value.Message
			})

			selectedTemplate.value = result.Id
		} catch (e) {
			console.error(e)
		} finally {
			isLoading.value = false
		}
	}
}

const CONFIRM_WIDTH = 232
const CONFIRM_HEIGHT = 140
const VIEWPORT_GAP = 8

// Окно подтверждения лежит в DOM внутри выпадашки селекта (иначе клик по нему
// закроет селект через v-click-outside), но позиционируется fixed — поэтому
// координаты считаем от иконки и сами удерживаем окно в пределах экрана
const openTemplateDeleteConfirm = (item: SelectItem, event: MouseEvent) => {
	const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
	const maxLeft = window.innerWidth - CONFIRM_WIDTH - VIEWPORT_GAP
	const openUp = rect.bottom + CONFIRM_HEIGHT + VIEWPORT_GAP > window.innerHeight

	templateConfirmStyle.value = {
		top: openUp
			? `${Math.max(VIEWPORT_GAP, rect.top - CONFIRM_HEIGHT - 4)}px`
			: `${rect.bottom + 4}px`,
		left: `${Math.max(VIEWPORT_GAP, Math.min(rect.right - CONFIRM_WIDTH, maxLeft))}px`
	}
	templateToDelete.value = { id: Number(item.id), label: item.label }

	window.addEventListener('scroll', closeTemplateConfirm, true)
}

const closeTemplateConfirm = () => {
	if (!templateToDelete.value) {
		return
	}

	templateToDelete.value = null
	window.removeEventListener('scroll', closeTemplateConfirm, true)
}

const confirmTemplateDelete = async () => {
	const target = templateToDelete.value
	if (!target || isTemplateDeleting.value) {
		return
	}

	try {
		isTemplateDeleting.value = true
		const isRemoved = await JobsApi.removeJobsTemplate(target.id)

		if (!isRemoved) {
			notify.danger(t('job_templates_title'), t('job_template_delete_error'))
			return
		}

		templates.value = templates.value.filter(template => template.Id !== target.id)
		if (selectedTemplate.value === target.id) {
			selectedTemplate.value = null
		}
		closeTemplateConfirm()
	} finally {
		isTemplateDeleting.value = false
	}
}

const isFormValid = computed(() =>
	!!filters.value.WorkerIds?.length
	&& !!filters.value.Message.trim()
	&& (!filters.value.NeedChecking || !!filters.value.ToDoDate)
)

watch(filters, () => {
	if (props.type === 'create') {
		localStorage.setItem('newJob', JSON.stringify(filters.value))
	}
}, {
	deep: true,
})

watch(selectedTemplate, (newVal) => {
	if (newVal) {
		const searched = templates.value.find(template => template.Id === newVal)
		const text = searched?.Text || ''
		if (!text) return

		filters.value.Message = filters.value.Message
			? `${filters.value.Message} ${text}`
			: text
	}
})

onMounted(() => {
	onOpen()
})

onUnmounted(() => {
	closeTemplateConfirm()
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))
</script>

<style scoped lang="scss">
.create-edit-job {
	display: flex;
	flex-direction: column;
	height: 100%;
	position: relative;
	border: 1px dashed transparent;
	border-radius: 8px;

	&--can-drop {
		border-color: #3F884F;
	}

	&--cannot-drop {
		border-color: #D05A4F;
	}

	&__drop-zone {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		border-radius: 8px;
		pointer-events: none;
		z-index: -1;

		&--can-drop {
			z-index: 1;
			background-color: rgba(#3F884F, 0.05);
		}

		&--cannot-drop {
			z-index: 1;
			background-color: rgba(#D05A4F, 0.05);
		}
	}

	&__header {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		justify-content: space-between;
		margin: 0 3px;
		padding: 12px 9px;
		border-bottom: 1px solid #D2D2D2;
		box-sizing: border-box;

		&-title {
			font-size: 14px;
			line-height: 18px;
			font-weight: 500;
			color: #4D4D4D;
		}
	}

	&__body {
		display: flex;
		flex: 1;
		min-height: 0;
		flex-direction: column;
		margin: 0 3px;
		padding: 8px 9px;
		overflow-y: auto;
		overscroll-behavior: contain;

		&-options {
			display: flex;
			align-items: center;
			justify-content: space-between;
			margin: 8px 0;

			&-checkbox {
				display: flex;
				align-items: center;
				gap: 8px;
			}

			&-date-clear {
				margin-left: -2px;
			}
		}

		&-attachments {
			display: flex;
			max-width: 100%;
			margin-top: 4px;

			&--have-files {
				flex-direction: column-reverse;

				.create-edit-job__body-files-block {
					margin-top: 4px;
				}
			}
		}

		&-files-block {
			display: flex;
			gap: 2px;
			max-width: 100%;
		}

		&-files {
			display: flex;
			flex-direction: column;
			gap: 4px;
			max-width: calc(100% - 20px);
		}

		&-files-item {
			display: flex;
			align-items: center;
			gap: 2px;

			&-icon {
				flex-shrink: 0;
			}

			&-name {
				text-overflow: ellipsis;
				overflow: hidden;
				white-space: nowrap;
				color: #979797;
				font-size: 14px;
				line-height: 18px;
				cursor: pointer;

				&:hover {
					color: #333;
				}
			}

			&-name-tooltip {
				min-width: 0;

				:deep(.app-tooltip__activator) {
					display: block;
					min-width: 0;
				}
			}

			&-delete {
				margin: -1px 2px 0 2px;
				flex-shrink: 0;
			}
		}

		&-tags-block {
			display: flex;
			align-items: center;
			flex-wrap: wrap;
			gap: 4px;

			.create-edit-job__body-tags-add {
				margin-right: 2px;
			}
		}

		&-tag {
			display: inline-flex;
			align-items: center;
			font-size: 14px;
			line-height: 18px;
			color: #979797;
			text-transform: lowercase;

			&-delete {
				margin: 0 0 0 2px;
			}
		}

		&::v-deep(.q-field__append) {
			height: 32px;
		}
	}

	&__textarea {
		height: 100%;
		margin-bottom: 8px;
	}

	&__templates {
		margin-bottom: 8px;
	}

	&__divider {
		flex-shrink: 0;
		height: 1px;
		margin: 0 3px;
		background-color: #D2D2D2;
	}

	&__template-confirm {
		position: fixed;
		z-index: 10002;
		width: 232px;
		background: #FFF;
		border-radius: 4px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	}

	&__upload-input {
		display: none;
	}

	&__footer {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		justify-content: flex-end;
		padding: 12px 12px max(12px, env(safe-area-inset-bottom));

		&-btn {
			background-color: #3F884F;
			color: #fff;
			padding: 7px 16px;
			border-radius: 8px;
			line-height: 16px;

			&::v-deep(.q-btn__content) {
				padding-top: 2px;
			}
		}
	}

	&__search {
		width: 100%;
		margin-bottom: 8px;

		&::v-deep(.q-field__control) {
			height: 32px;
		}
	}
}
</style>
